// Timer.tsx
import { FC, useEffect, useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from '../recording/RecordingState';
import { createUseStyles } from 'react-jss';
import { Stopwatch } from '../utils/Stopwatch';

export type IStyleProps = {
    size?: 'small' | 'normal'
};

export type ITimerProps = IStyleProps & {
    onPause?: (seconds: number) => void;
};

type IStyleKeys = 'wrapper' | 'colon' | 'small';
const useStyles = createUseStyles<IStyleKeys, IStyleProps>({
    wrapper: {
        width: '100%',
        textAlign: 'center',

        '& > span': {
            display: 'inline-block',
            width: ({ size }) => size === 'small' ? 12 : 31
        }
    },

    colon: {
        width: 10,
        display: 'inline-block',
        position: 'relative',
        margin: '0 2px 5px 3px',
        verticalAlign: 'middle',
        '&::before, &::after': {
            content: '""',
            display: 'block',
            width: 8,
            height: 8,
            background: 'currentColor',
            borderRadius: '50%',
            position: 'absolute',
            left: 0
        },
        '&::before': {
            top: 2
        },
        '&::after': {
            bottom: 4
        }
    },

    small: {
        width: 5,
        '&::before, &::after': {
            width: 4,
            height: 4
        },
        '&::before': {
            top: 2
        },
        '&::after': {
            bottom: 0
        }
    }
});

export const Colon: FC<ITimerProps> = ({ size }) => {
    const classes = useStyles({ size });
    return (
        <div className={`${classes.colon} ${size === 'small' ? classes.small : ''}`} />
    );
};

export const Timer: FC<ITimerProps> = ({ size = 'normal', onPause }) => {
    const classes = useStyles({ size });

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const [seconds, setSeconds] = useState(0);
    const [stopwatch] = useState(() => new Stopwatch());

    // Distinguishes a fresh start (reset to zero) from resuming after a pause (continue).
    const wasRecordingRef = useRef(false);

    const isRecording = recordingState.isRecording();
    const isPaused = recordingState.isPaused();
    const isActive = isRecording && !isPaused;

    // Drive the stopwatch from the recording lifecycle and refresh the display
    // immediately on each transition.
    useEffect(() => {
        if (isActive) {
            if (wasRecordingRef.current) {
                stopwatch.resume();
            } else {
                stopwatch.start();
            }
        } else if (isRecording && isPaused) {
            stopwatch.pause();
        } else {
            stopwatch.reset();
        }
        wasRecordingRef.current = isRecording;
        setSeconds(Math.floor(stopwatch.elapsedMs() / 1000));
    }, [isRecording, isPaused]); // eslint-disable-line react-hooks/exhaustive-deps

    // While recording, derive the displayed time from the wall clock on each tick.
    // Recomputing (rather than incrementing a counter, or adding "catch-up" time on
    // foreground) means a throttled background timer or a duplicate event self-corrects
    // on the next tick instead of inflating the count — the root cause of IH-6926.
    useEffect(() => {
        if (!isActive) {
            return;
        }
        const intervalId = setInterval(() => {
            setSeconds(Math.floor(stopwatch.elapsedMs() / 1000));
        }, 250);
        return () => clearInterval(intervalId);
    }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report the paused-at duration for analytics, once per pause transition.
    // (onPause is intentionally omitted from deps: it gets a fresh identity each
    // render, and including it would re-fire the event on unrelated re-renders while
    // still paused.)
    useEffect(() => {
        if (isPaused && onPause) {
            onPause(Math.floor(stopwatch.elapsedMs() / 1000));
        }
    }, [isPaused]); // eslint-disable-line react-hooks/exhaustive-deps

    const formatTime = (totalSeconds: number) => {
        const h = Math.floor(totalSeconds / 3600);
        const m = Math.floor((totalSeconds % 3600) / 60);
        const s = totalSeconds % 60;
        const pad = (n: number) => n.toString().padStart(2, '0');

        return (
            <span className={classes.wrapper}>
                {h > 0 ? <><span>{h}</span><Colon size={size} /></> : ''}
                <span>{pad(m).substring(0, 1)}</span><span>{pad(m).substring(1, 2)}</span><Colon size={size} />
                <span>{pad(s).substring(0, 1)}</span><span>{pad(s).substring(1, 2)}</span>
            </span>
        );
    };

    return formatTime(seconds);
};
